import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ExternalLink,
  HelpCircle,
  MessageCircleQuestion,
  ToggleLeft,
  ToggleRight,
} from 'lucide-react';
import {
  fetchAdminFaqs,
  fetchAdminSection,
  patchAdminSection,
  type AdminFaqItemRow,
  type AdminFaqSectionRow,
} from '../../api/admin';
import type { FaqItem } from '../../types/faq';
import {
  AdminDetailCard,
  AdminEmptyState,
  AdminKpiStrip,
  AdminPageLayout,
  AdminSearchInput,
  AdminStatusBadge,
  AdminTablePanel,
} from '../../components/admin/AdminPageLayout';
import { Illustration } from '../../components/common/Illustration';
import { LoadingState, ErrorState } from '../../components/states/StateViews';

function FaqSectionCard({
  section,
  onSelect,
}: {
  section: AdminFaqSectionRow;
  onSelect: () => void;
}) {
  return (
    <button type="button" className="wfx-admin-faq-section-card glass-surface" onClick={onSelect}>
      <Illustration context="faq.general" size="sm" className="wfx-admin-page-card-illustration" />
      <div className="wfx-admin-page-card-copy">
        <strong>{section.title ?? section.pageLabel}</strong>
        <span className="wfx-muted">{section.pageRoute}</span>
      </div>
      <div className="wfx-admin-page-card-stats">
        <span><MessageCircleQuestion size={14} aria-hidden="true" /> {section.questionCount} questions</span>
        <AdminStatusBadge status={section.isEnabled ? 'enabled' : 'disabled'} />
      </div>
    </button>
  );
}

function FaqSectionDetailCard({
  sectionId,
  onClose,
}: {
  sectionId: string | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin-section', sectionId],
    queryFn: () => fetchAdminSection(sectionId!),
    enabled: Boolean(sectionId),
  });

  const update = useMutation({
    mutationFn: (body: Parameters<typeof patchAdminSection>[1]) => patchAdminSection(sectionId!, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-faqs'] });
      void qc.invalidateQueries({ queryKey: ['admin-section', sectionId] });
    },
  });

  const section = data?.section;
  const items = (section?.configuration?.items as FaqItem[] | undefined) ?? [];

  return (
    <AdminDetailCard
      open={Boolean(sectionId)}
      onClose={onClose}
      ariaLabel="FAQ section details"
      breadcrumb="FAQ block"
      title={section?.title ?? 'Loading…'}
      illustration="faq.general"
      subtitle={
        section ? (
          <>
            <AdminStatusBadge status={section.isEnabled ? 'enabled' : 'disabled'} />
            <span className="wfx-muted wfx-admin-subline">{section.pageLabel} · {items.length} questions</span>
          </>
        ) : undefined
      }
    >
      {isLoading && <LoadingState label="Loading FAQ section…" />}
      {isError && <ErrorState message="Could not load FAQ section." onRetry={() => void refetch()} />}

      {section && (
        <>
          <dl className="wfx-admin-meta-list wfx-admin-meta-list--grid">
            <div><dt>Page</dt><dd>{section.pageLabel}</dd></div>
            <div><dt>Order</dt><dd>{section.displayOrder}</dd></div>
            <div><dt>Questions</dt><dd>{items.length}</dd></div>
            <div>
              <dt>Route</dt>
              <dd>
                {section.pageRoute && (
                  <Link to={section.pageRoute} className="secondary-action compact" target="_blank">
                    {section.pageRoute}
                    <ExternalLink size={12} aria-hidden="true" />
                  </Link>
                )}
              </dd>
            </div>
          </dl>

          <div className="wfx-admin-drawer-columns">
            <div className="wfx-admin-drawer-section">
              <h3>Section settings</h3>
              <div className="wfx-admin-form">
                <label className="wfx-admin-field">
                  <span>Title</span>
                  <input
                    defaultValue={section.title ?? ''}
                    onBlur={(e) => {
                      const value = e.target.value.trim();
                      if (value !== (section.title ?? '')) update.mutate({ title: value || null });
                    }}
                  />
                </label>
                <label className="wfx-admin-field">
                  <span>Subtitle</span>
                  <input
                    defaultValue={section.subtitle ?? ''}
                    onBlur={(e) => {
                      const value = e.target.value.trim();
                      if (value !== (section.subtitle ?? '')) update.mutate({ subtitle: value || null });
                    }}
                  />
                </label>
                <button
                  type="button"
                  className="secondary-action compact"
                  disabled={update.isPending}
                  onClick={() => update.mutate({ isEnabled: !section.isEnabled })}
                >
                  {section.isEnabled ? (
                    <><ToggleRight size={16} aria-hidden="true" /> Disable block</>
                  ) : (
                    <><ToggleLeft size={16} aria-hidden="true" /> Enable block</>
                  )}
                </button>
              </div>
            </div>

            <div className="wfx-admin-drawer-section">
              <h3>Questions in this block</h3>
              <div className="wfx-admin-faq-item-list">
                {items.map((item, index) => (
                  <article key={`${section.id}-${index}`} className="wfx-admin-faq-item-preview glass-surface">
                    <strong>{item.q}</strong>
                    <p className="wfx-muted">{item.a}</p>
                    {item.visual && <span className="chip-pill">{item.visual}</span>}
                  </article>
                ))}
                {items.length === 0 && <AdminEmptyState message="No questions in this block yet." />}
              </div>
            </div>
          </div>
        </>
      )}
    </AdminDetailCard>
  );
}

function FaqItemDetailCard({
  item,
  onClose,
}: {
  item: AdminFaqItemRow | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ['admin-section', item?.sectionId],
    queryFn: () => fetchAdminSection(item!.sectionId),
    enabled: Boolean(item?.sectionId),
  });

  const update = useMutation({
    mutationFn: (nextItems: FaqItem[]) =>
      patchAdminSection(item!.sectionId, {
        configuration: { ...(data?.section.configuration ?? {}), items: nextItems },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-faqs'] });
      void qc.invalidateQueries({ queryKey: ['admin-section', item?.sectionId] });
    },
  });

  const section = data?.section;
  const items = (section?.configuration?.items as FaqItem[] | undefined) ?? [];
  const current = item ? items[item.index] : null;

  const saveItem = (patch: Partial<FaqItem>) => {
    if (!item || !current) return;
    const nextItems = items.map((entry, index) =>
      index === item.index ? { ...entry, ...patch } : entry,
    );
    update.mutate(nextItems);
  };

  return (
    <AdminDetailCard
      open={Boolean(item)}
      onClose={onClose}
      ariaLabel="FAQ item details"
      breadcrumb="FAQ question"
      title={current?.q ?? item?.question ?? 'Loading…'}
      illustration="faq.general"
      subtitle={
        item ? (
          <span className="wfx-muted wfx-admin-subline">
            {item.pageLabel} · {item.sectionTitle ?? item.sectionId}
          </span>
        ) : undefined
      }
    >
      {item && current && (
        <>
          <dl className="wfx-admin-meta-list wfx-admin-meta-list--grid">
            <div><dt>Page</dt><dd>{item.pageLabel}</dd></div>
            <div><dt>Visual</dt><dd>{current.visual ?? 'text'}</dd></div>
            <div><dt>Icon</dt><dd>{current.icon ?? '—'}</dd></div>
            <div><dt>Block status</dt><dd><AdminStatusBadge status={item.isEnabled ? 'enabled' : 'disabled'} /></dd></div>
          </dl>

          <div className="wfx-admin-form">
            <label className="wfx-admin-field">
              <span>Question</span>
              <input
                defaultValue={current.q}
                onBlur={(e) => {
                  const value = e.target.value.trim();
                  if (value && value !== current.q) saveItem({ q: value });
                }}
              />
            </label>
            <label className="wfx-admin-field">
              <span>Answer</span>
              <textarea
                rows={4}
                className="wfx-admin-textarea"
                defaultValue={current.a}
                onBlur={(e) => {
                  const value = e.target.value.trim();
                  if (value && value !== current.a) saveItem({ a: value });
                }}
              />
            </label>
            {item.pageRoute && (
              <Link to={item.pageRoute} className="secondary-action compact" target="_blank">
                View on site
                <ExternalLink size={12} aria-hidden="true" />
              </Link>
            )}
          </div>
        </>
      )}
    </AdminDetailCard>
  );
}

export function AdminFaqsScreen() {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [pageFilter, setPageFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(null);
  const [selectedItem, setSelectedItem] = useState<AdminFaqItemRow | null>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [query]);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin-faqs', debouncedQuery, pageFilter, statusFilter],
    queryFn: () =>
      fetchAdminFaqs({
        q: debouncedQuery || undefined,
        page: pageFilter,
        status: statusFilter,
      }),
  });

  const sections = data?.sections ?? [];
  const items = data?.items ?? [];
  const stats = data?.stats;

  const pageOptions = useMemo(() => {
    const pages = new Map<string, string>();
    for (const section of sections) {
      pages.set(section.page, section.pageLabel ?? section.page);
    }
    return Array.from(pages.entries());
  }, [sections]);

  const kpiItems = useMemo(
    () => [
      { label: 'FAQ blocks', value: stats?.sections ?? 0 },
      { label: 'Questions', value: stats?.questions ?? 0 },
      { label: 'Enabled', value: stats?.enabled ?? 0 },
      { label: 'Pages', value: stats?.pages ?? 0 },
    ],
    [stats],
  );

  if (isLoading && !data) return <LoadingState label="Loading FAQs…" />;
  if (isError) return <ErrorState message="Could not load FAQs." onRetry={() => void refetch()} />;

  return (
    <>
      <AdminPageLayout
        breadcrumb="Admin / Content / FAQs"
        title="FAQs"
        description="Manage visual FAQ blocks embedded across public pages — questions, answers, and section settings."
        icon={HelpCircle}
        illustration="faq.general"
      >
        <AdminKpiStrip items={kpiItems} />

        <div className="wfx-admin-faq-section-grid">
          {sections.map((section) => (
            <FaqSectionCard
              key={section.id}
              section={section}
              onSelect={() => {
                setSelectedItem(null);
                setSelectedSectionId(section.id);
              }}
            />
          ))}
        </div>

        <AdminTablePanel
          title="All questions"
          toolbar={(
            <div className="wfx-admin-toolbar">
              <AdminSearchInput
                value={query}
                onChange={setQuery}
                placeholder="Search questions, answers, sections…"
              />
              <select
                className="wfx-admin-select"
                value={pageFilter}
                onChange={(e) => setPageFilter(e.target.value)}
                aria-label="Filter by page"
              >
                <option value="all">All pages</option>
                {pageOptions.map(([id, label]) => (
                  <option key={id} value={id}>{label}</option>
                ))}
              </select>
              <select
                className="wfx-admin-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                aria-label="Filter by status"
              >
                <option value="all">All statuses</option>
                <option value="enabled">Enabled</option>
                <option value="disabled">Disabled</option>
              </select>
            </div>
          )}
        >
          <table className="wfx-admin-table">
            <thead>
              <tr><th>Question</th><th>Page</th><th>Section</th><th>Visual</th><th>Status</th></tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <tr
                  key={row.id}
                  className="wfx-admin-table-row-clickable"
                  onClick={() => {
                    setSelectedSectionId(null);
                    setSelectedItem(row);
                  }}
                >
                  <td>
                    <strong>{row.question}</strong>
                    <span className="wfx-muted wfx-admin-subline">{row.answer}</span>
                  </td>
                  <td>{row.pageLabel}</td>
                  <td>{row.sectionTitle ?? row.sectionId}</td>
                  <td>{row.visual ?? 'text'}</td>
                  <td><AdminStatusBadge status={row.isEnabled ? 'enabled' : 'disabled'} /></td>
                </tr>
              ))}
            </tbody>
          </table>
          {items.length === 0 && <AdminEmptyState message="No FAQ questions match your filters." />}
        </AdminTablePanel>
      </AdminPageLayout>

      <FaqSectionDetailCard
        sectionId={selectedSectionId}
        onClose={() => setSelectedSectionId(null)}
      />
      <FaqItemDetailCard
        item={selectedItem}
        onClose={() => setSelectedItem(null)}
      />
    </>
  );
}
